// API bindings: rulebook_admin (Auto generated)

"use strict";

import { RequestErrorHandler, RequestParams, CommonAuthenticatedErrorHandler } from "@asanrom/request-browser";
import { getApiUrl, generateURIQuery } from "./utils";
import { RulebookPublisherList, RulebookPublisherItem, AddRulebookPublisherBody, ModifyRulebookPublisherBody, RulebookPublisherCheckResult, RulebookPublisherSyncResult, RulebookConflictList, RulebookConflictDetection, RulebookConflictResolveBody, RulebookAutomationPolicy, RulebookAutomationPolicyBody, RulebookList, RulebookVersion, RulebookCreateBody, RulebookModifyBody, RulebookDocument, RulebookText, RulebookRuleList, RulebookRule, RulebookRuleCreateBody, RulebookRuleModifyBody, RulebookExtractBody, RulebookRuleReviewBody, RulebookRulesReviewResult, RulebookRulesReviewBody } from "./definitions";

export class ApiRulebookAdmin {
    /**
     * Method: GET
     * Path: /admin/rulebook-publishers
     * Lists the trusted rulebook publishers
     * Requires permission: admin.ds
     * @returns The request parameters
     */
    public static ListRulebookPublishers(): RequestParams<RulebookPublisherList, ListRulebookPublishersErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/admin/rulebook-publishers`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook-publishers
     * Adds a trusted rulebook publisher. Nothing is imported from it yet: that comes when it is synchronized
     * Requires permission: admin.ds
     * @param body Body parameters
     * @returns The request parameters
     */
    public static AddRulebookPublisher(body: AddRulebookPublisherBody): RequestParams<RulebookPublisherItem, AddRulebookPublisherErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook-publishers`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_URL", handler.badRequestInvalidUrl)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "SELF", handler.badRequestSelf)
                    .add(400, "DUPLICATED_ID", handler.badRequestDuplicatedId)
                    .add(400, "INVALID_ID", handler.badRequestInvalidId)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook-publishers/{id}
     * Modifies the name or the URL of a trusted rulebook publisher. The identity cannot change:
     * that would be another publisher
     * Requires permission: admin.ds
     * @param id Identity of the publisher
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ModifyRulebookPublisher(id: string, body: ModifyRulebookPublisherBody): RequestParams<void, ModifyRulebookPublisherErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook-publishers/${encodeURIComponent(id)}`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "PUBLISHER_NOT_FOUND", handler.notFoundPublisherNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_URL", handler.badRequestInvalidUrl)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: DELETE
     * Path: /admin/rulebook-publishers/{id}
     * Removes a trusted rulebook publisher. Nothing more is asked of it, but what was already
     * received from it stays
     * Requires permission: admin.ds
     * @param id Identity of the publisher
     * @returns The request parameters
     */
    public static DeleteRulebookPublisher(id: string): RequestParams<void, DeleteRulebookPublisherErrorHandler> {
        return {
            method: "DELETE",
            url: getApiUrl(`/admin/rulebook-publishers/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "PUBLISHER_NOT_FOUND", handler.notFoundPublisherNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook-publishers/{id}/check
     * Checks a trusted rulebook publisher: asks it for the rulebooks it publishes, making sure the
     * participant that answers is the one it was added for. Nothing is imported
     * Requires permission: admin.ds
     * @param id Identity of the publisher
     * @returns The request parameters
     */
    public static CheckRulebookPublisher(id: string): RequestParams<RulebookPublisherCheckResult, CheckRulebookPublisherErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook-publishers/${encodeURIComponent(id)}/check`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "PUBLISHER_NOT_FOUND", handler.notFoundPublisherNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook-publishers/{id}/sync
     * Synchronizes a trusted rulebook publisher now, instead of waiting for the periodic
     * synchronization: the versions it publishes that were not received yet are stored as
     * inherited, waiting for acceptance, with their rules pending review
     * Requires permission: admin.ds
     * @param id Identity of the publisher
     * @returns The request parameters
     */
    public static SyncRulebookPublisher(id: string): RequestParams<RulebookPublisherSyncResult, SyncRulebookPublisherErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook-publishers/${encodeURIComponent(id)}/sync`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "PUBLISHER_NOT_FOUND", handler.notFoundPublisherNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /admin/rulebook-conflicts
     * Lists the conflicts between rules of different rulebooks, telling which rule each one sets aside.
     * Undecided, the own rule prevails over an inherited one, and between two own or two
     * inherited rules, the one approved first
     * Requires permission: admin.ds
     * @returns The request parameters
     */
    public static ListRulebookConflicts(): RequestParams<RulebookConflictList, ListRulebookConflictsErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/admin/rulebook-conflicts`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /admin/rulebook-conflicts/detection
     * Tells how the last detection of conflicts went, or that one is running
     * Requires permission: admin.ds
     * @returns The request parameters
     */
    public static GetRulebookConflictDetection(): RequestParams<RulebookConflictDetection, GetRulebookConflictDetectionErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/admin/rulebook-conflicts/detection`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook-conflicts/detect
     * Looks for conflicts between the rules of different rulebooks: the own approved ones and the inherited ones, in force or
     * waiting for acceptance.
     * Requires permission: admin.ds
     * @returns The request parameters
     */
    public static DetectRulebookConflicts(): RequestParams<RulebookConflictDetection, DetectRulebookConflictsErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook-conflicts/detect`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(400, "ALREADY_RUNNING", handler.badRequestAlreadyRunning)
                    .add(400, "ASSISTANT_DISABLED", handler.badRequestAssistantDisabled)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook-conflicts/{id}/resolve
     * Decides a conflict: that it is not one, so both rules apply, or which of the two prevails,
     * setting the other one aside. A decision can be changed afterwards
     * Requires permission: admin.ds
     * @param id Identifier of the conflict
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ResolveRulebookConflict(id: string, body: RulebookConflictResolveBody): RequestParams<void, ResolveRulebookConflictErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook-conflicts/${encodeURIComponent(id)}/resolve`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "CONFLICT_NOT_FOUND", handler.notFoundConflictNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_RESOLUTION", handler.badRequestInvalidResolution)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /admin/rulebook-automation
     * Gets the automation policy of the rulebook, and the rules it is declared to apply
     * Requires permission: admin.ds
     * @returns The request parameters
     */
    public static GetRulebookAutomationPolicy(): RequestParams<RulebookAutomationPolicy, GetRulebookAutomationPolicyErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/admin/rulebook-automation`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook-automation
     * Chooses the automation policy of the rulebook. From then on it is evaluated on every offer
     * received, before the policy of the dataset, and it can only stop the offer
     * Requires permission: admin.ds
     * @param body Body parameters
     * @returns The request parameters
     */
    public static SetRulebookAutomationPolicy(body: RulebookAutomationPolicyBody): RequestParams<void, SetRulebookAutomationPolicyErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook-automation`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(400, "POLICY_NOT_FOUND", handler.badRequestPolicyNotFound)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /admin/rulebook
     * Lists the rulebook versions stored in the connector
     * Requires permission: admin.ds
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListRulebooks(queryParams: ListRulebooksQueryParameters): RequestParams<RulebookList, ListRulebooksErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/admin/rulebook` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook
     * Creates a rulebook version, in draft status.
     * Requires permission: admin.ds
     * @param body Body parameters
     * @returns The request parameters
     */
    public static CreateRulebook(body: RulebookCreateBody): RequestParams<RulebookVersion, CreateRulebookErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(400, "DUPLICATED_VERSION", handler.badRequestDuplicatedVersion)
                    .add(400, "RULEBOOK_NOT_EDITABLE", handler.badRequestRulebookNotEditable)
                    .add(400, "RULEBOOK_NOT_FOUND", handler.badRequestRulebookNotFound)
                    .add(400, "INVALID_PROMPT_ADDENDUM", handler.badRequestInvalidPromptAddendum)
                    .add(400, "INVALID_DOCUMENT_URL", handler.badRequestInvalidDocumentUrl)
                    .add(400, "INVALID_SOURCE_TYPE", handler.badRequestInvalidSourceType)
                    .add(400, "INVALID_EFFECTIVE_DATE", handler.badRequestInvalidEffectiveDate)
                    .add(400, "INVALID_VERSION", handler.badRequestInvalidVersion)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /admin/rulebook/{id}
     * Gets a rulebook version.
     * The extracted text is not included: it is served by its own endpoint, since it can be large
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @returns The request parameters
     */
    public static GetRulebook(id: string): RequestParams<RulebookVersion, GetRulebookErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook/{id}
     * Modifies a rulebook version.
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ModifyRulebook(id: string, body: RulebookModifyBody): RequestParams<void, ModifyRulebookErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "DUPLICATED_VERSION", handler.badRequestDuplicatedVersion)
                    .add(400, "NOT_A_DRAFT", handler.badRequestNotADraft)
                    .add(400, "RULEBOOK_NOT_EDITABLE", handler.badRequestRulebookNotEditable)
                    .add(400, "INVALID_PROMPT_ADDENDUM", handler.badRequestInvalidPromptAddendum)
                    .add(400, "INVALID_DOCUMENT_URL", handler.badRequestInvalidDocumentUrl)
                    .add(400, "INVALID_SOURCE_TYPE", handler.badRequestInvalidSourceType)
                    .add(400, "INVALID_EFFECTIVE_DATE", handler.badRequestInvalidEffectiveDate)
                    .add(400, "INVALID_VERSION", handler.badRequestInvalidVersion)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: DELETE
     * Path: /admin/rulebook/{id}
     * Deletes a rulebook version, along with the rules extracted from it.
     * The version in force cannot be deleted, and neither can a version some evaluation was
     * made against, since the evaluation cites it as evidence
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @returns The request parameters
     */
    public static DeleteRulebook(id: string): RequestParams<void, DeleteRulebookErrorHandler> {
        return {
            method: "DELETE",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "CITED_BY_EVIDENCE", handler.badRequestCitedByEvidence)
                    .add(400, "ACTIVE_VERSION", handler.badRequestActiveVersion)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /admin/rulebook/{id}/document
     * Gets a link to download the original rulebook document
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @returns The request parameters
     */
    public static GetRulebookDocument(id: string): RequestParams<RulebookDocument, GetRulebookDocumentErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/document`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook/{id}/document
     * Uploads the original rulebook document, for a version whose source type is PDF or Markdown.
     * The text is extracted on upload, since the indexing and the rule extraction work on it:
     * a document whose text cannot be read is rejected instead of being stored half ingested.
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @param formParams FromData parameters
     * @returns The request parameters
     */
    public static UploadRulebookDocument(id: string, formParams: UploadRulebookDocumentFormParameters): RequestParams<RulebookVersion, UploadRulebookDocumentErrorHandler> {
        const formData = new FormData();

        formData.append("document", formParams.document);

        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/document`),
            form: formData,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(500, "STORAGE_ERROR", handler.serverErrorStorageError)
                    .add(500, "TEXT_EXTRACTION_FAILED", handler.serverErrorTextExtractionFailed)
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_FORMAT", handler.badRequestInvalidFormat)
                    .add(400, "NO_FILE", handler.badRequestNoFile)
                    .add(400, "NO_DOCUMENT_EXPECTED", handler.badRequestNoDocumentExpected)
                    .add(400, "NOT_A_DRAFT", handler.badRequestNotADraft)
                    .add(400, "RULEBOOK_NOT_EDITABLE", handler.badRequestRulebookNotEditable)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /admin/rulebook/{id}/text
     * Gets the text extracted from the rulebook document.
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @returns The request parameters
     */
    public static GetRulebookText(id: string): RequestParams<RulebookText, GetRulebookTextErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/text`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook/{id}/activate
     * Puts a rulebook version in force, archiving the version that was in force before.
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @returns The request parameters
     */
    public static ActivateRulebook(id: string): RequestParams<RulebookVersion, ActivateRulebookErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/activate`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(500, "STORAGE_ERROR", handler.serverErrorStorageError)
                    .add(500, "TEXT_EXTRACTION_FAILED", handler.serverErrorTextExtractionFailed)
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INHERITED_VERSION", handler.badRequestInheritedVersion)
                    .add(400, "DOCUMENT_NOT_SUPPORTED", handler.badRequestDocumentNotSupported)
                    .add(400, "DOCUMENT_NOT_REACHABLE", handler.badRequestDocumentNotReachable)
                    .add(400, "NO_DOCUMENT", handler.badRequestNoDocument)
                    .add(400, "ALREADY_ACTIVE", handler.badRequestAlreadyActive)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook/{id}/deactivate
     * Takes the version out of force and archives it, leaving the connector with no rulebook
     * governing until another version is activated. It is the way out for a connector that
     * stops taking part in the data space, so it is not done by mistake
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @returns The request parameters
     */
    public static DeactivateRulebook(id: string): RequestParams<RulebookVersion, DeactivateRulebookErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/deactivate`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "NOT_ACTIVE", handler.badRequestNotActive)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook/{id}/accept
     * Records the express acceptance of a rulebook version, as the governance guide requires
     * from every participant joining the data space. Who accepted it and when are kept as the
     * evidence of the acceptance.
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @returns The request parameters
     */
    public static AcceptRulebook(id: string): RequestParams<RulebookVersion, AcceptRulebookErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/accept`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "ALREADY_ACCEPTED", handler.badRequestAlreadyAccepted)
                    .add(400, "NOT_ACTIVE", handler.badRequestNotActive)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /admin/rulebook/{id}/rules
     * Lists the rules of a rulebook version
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListRules(id: string, queryParams: ListRulesQueryParameters): RequestParams<RulebookRuleList, ListRulesErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/rules` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook/{id}/rules
     * Adds a rule to a rulebook version. The rule is created pending review,
     * so it does not take part in anything until a person approves it
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @param body Body parameters
     * @returns The request parameters
     */
    public static CreateRule(id: string, body: RulebookRuleCreateBody): RequestParams<RulebookRule, CreateRuleErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/rules`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "RULE_IN_FORCE", handler.badRequestRuleInForce)
                    .add(400, "INVALID_CHECK_EXPRESSION", handler.badRequestInvalidCheckExpression)
                    .add(400, "INVALID_ENFORCEMENT", handler.badRequestInvalidEnforcement)
                    .add(400, "INVALID_SEVERITY", handler.badRequestInvalidSeverity)
                    .add(400, "INVALID_SECTION", handler.badRequestInvalidSection)
                    .add(400, "INVALID_TEXT", handler.badRequestInvalidText)
                    .add(400, "INVALID_SCOPE", handler.badRequestInvalidScope)
                    .add(400, "INVALID_CODE", handler.badRequestInvalidCode)
                    .add(400, "RULEBOOK_NOT_EDITABLE", handler.badRequestRulebookNotEditable)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /admin/rulebook/{id}/rules/{ruleId}
     * Gets a rule of a rulebook version
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @param ruleId Rule identifier
     * @returns The request parameters
     */
    public static GetRule(id: string, ruleId: string): RequestParams<RulebookRule, GetRuleErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/rules/${encodeURIComponent(ruleId)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook/{id}/rules/{ruleId}
     * Modifies a rule.
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @param ruleId Rule identifier
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ModifyRule(id: string, ruleId: string, body: RulebookRuleModifyBody): RequestParams<void, ModifyRuleErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/rules/${encodeURIComponent(ruleId)}`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "RULE_IN_FORCE", handler.badRequestRuleInForce)
                    .add(400, "INVALID_CHECK_EXPRESSION", handler.badRequestInvalidCheckExpression)
                    .add(400, "INVALID_ENFORCEMENT", handler.badRequestInvalidEnforcement)
                    .add(400, "INVALID_SEVERITY", handler.badRequestInvalidSeverity)
                    .add(400, "INVALID_SECTION", handler.badRequestInvalidSection)
                    .add(400, "INVALID_TEXT", handler.badRequestInvalidText)
                    .add(400, "INVALID_SCOPE", handler.badRequestInvalidScope)
                    .add(400, "INVALID_CODE", handler.badRequestInvalidCode)
                    .add(400, "RULEBOOK_NOT_EDITABLE", handler.badRequestRulebookNotEditable)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: DELETE
     * Path: /admin/rulebook/{id}/rules/{ruleId}
     * Deletes a rule of a rulebook version
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @param ruleId Rule identifier
     * @returns The request parameters
     */
    public static DeleteRule(id: string, ruleId: string): RequestParams<void, DeleteRuleErrorHandler> {
        return {
            method: "DELETE",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/rules/${encodeURIComponent(ruleId)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "RULE_IN_FORCE", handler.badRequestRuleInForce)
                    .add(400, "INVALID_CHECK_EXPRESSION", handler.badRequestInvalidCheckExpression)
                    .add(400, "INVALID_ENFORCEMENT", handler.badRequestInvalidEnforcement)
                    .add(400, "INVALID_SEVERITY", handler.badRequestInvalidSeverity)
                    .add(400, "INVALID_SECTION", handler.badRequestInvalidSection)
                    .add(400, "INVALID_TEXT", handler.badRequestInvalidText)
                    .add(400, "INVALID_SCOPE", handler.badRequestInvalidScope)
                    .add(400, "INVALID_CODE", handler.badRequestInvalidCode)
                    .add(400, "RULEBOOK_NOT_EDITABLE", handler.badRequestRulebookNotEditable)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook/{id}/extract
     * Reads the text of a rulebook version and stores the rules it states about a scope,
     * or about every scope when none is given.
     * The rules are stored pending review: nothing extracted here takes part in anything
     * until a person approves it.
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ExtractRules(id: string, body: RulebookExtractBody): RequestParams<RulebookVersion, ExtractRulesErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/extract`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "RULEBOOK_NOT_EDITABLE", handler.badRequestRulebookNotEditable)
                    .add(400, "ALREADY_RUNNING", handler.badRequestAlreadyRunning)
                    .add(400, "NO_TEXT", handler.badRequestNoText)
                    .add(400, "INVALID_SCOPE", handler.badRequestInvalidScope)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook/{id}/rules/{ruleId}/review
     * Reviews a rule, approving or rejecting it.
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @param ruleId Rule identifier
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ReviewRule(id: string, ruleId: string, body: RulebookRuleReviewBody): RequestParams<RulebookRule, ReviewRuleErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/rules/${encodeURIComponent(ruleId)}/review`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_SCOPE", handler.badRequestInvalidScope)
                    .add(400, "INVALID_STATUS", handler.badRequestInvalidStatus)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/rulebook/{id}/rules/review
     * Reviews at once every rule of a rulebook version that is still pending.
     * Requires permission: admin.ds
     * @param id Identifier of the rulebook version
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ReviewRules(id: string, body: RulebookRulesReviewBody): RequestParams<RulebookRulesReviewResult, ReviewRulesErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/rulebook/${encodeURIComponent(id)}/rules/review`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NO_DOCUMENT", handler.notFoundNoDocument)
                    .add(404, "RULE_NOT_FOUND", handler.notFoundRuleNotFound)
                    .add(404, "RULEBOOK_NOT_FOUND", handler.notFoundRulebookNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_SCOPE", handler.badRequestInvalidScope)
                    .add(400, "INVALID_STATUS", handler.badRequestInvalidStatus)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }
}

/**
 * Error handler for ListRulebookPublishers
 */
export type ListRulebookPublishersErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for AddRulebookPublisher
 */
export type AddRulebookPublisherErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid identity
     */
    badRequestInvalidId: () => void;

    /**
     * That connector is already a publisher
     */
    badRequestDuplicatedId: () => void;

    /**
     * That is this connector itself
     */
    badRequestSelf: () => void;

    /**
     * Invalid name
     */
    badRequestInvalidName: () => void;

    /**
     * Invalid URL
     */
    badRequestInvalidUrl: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for ModifyRulebookPublisher
 */
export type ModifyRulebookPublisherErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid name
     */
    badRequestInvalidName: () => void;

    /**
     * Invalid URL
     */
    badRequestInvalidUrl: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * The publisher does not exist
     */
    notFoundPublisherNotFound: () => void;
};

/**
 * Error handler for DeleteRulebookPublisher
 */
export type DeleteRulebookPublisherErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * The publisher does not exist
     */
    notFoundPublisherNotFound: () => void;
};

/**
 * Error handler for CheckRulebookPublisher
 */
export type CheckRulebookPublisherErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * The publisher does not exist
     */
    notFoundPublisherNotFound: () => void;
};

/**
 * Error handler for SyncRulebookPublisher
 */
export type SyncRulebookPublisherErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * The publisher does not exist
     */
    notFoundPublisherNotFound: () => void;
};

/**
 * Error handler for ListRulebookConflicts
 */
export type ListRulebookConflictsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetRulebookConflictDetection
 */
export type GetRulebookConflictDetectionErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for DetectRulebookConflicts
 */
export type DetectRulebookConflictsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The assistant is not enabled in this connector
     */
    badRequestAssistantDisabled: () => void;

    /**
     * A detection is already running
     */
    badRequestAlreadyRunning: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for ResolveRulebookConflict
 */
export type ResolveRulebookConflictErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The decision is not one of the recognized ones
     */
    badRequestInvalidResolution: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * The conflict does not exist
     */
    notFoundConflictNotFound: () => void;
};

/**
 * Error handler for GetRulebookAutomationPolicy
 */
export type GetRulebookAutomationPolicyErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for SetRulebookAutomationPolicy
 */
export type SetRulebookAutomationPolicyErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The negotiation automation policy does not exist
     */
    badRequestPolicyNotFound: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for ListRulebooks
 */
export type ListRulebooksErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Query parameters for ListRulebooks
 */
export interface ListRulebooksQueryParameters {
    /**
     * Page number (1-based). Default: 1
     */
    page?: number;

    /**
     * Items per page. Default: 10, Max: 256
     */
    pageSize?: number;
}

/**
 * Error handler for CreateRulebook
 */
export type CreateRulebookErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid or missing name
     */
    badRequestInvalidName: () => void;

    /**
     * Invalid or missing version
     */
    badRequestInvalidVersion: () => void;

    /**
     * Invalid effective date
     */
    badRequestInvalidEffectiveDate: () => void;

    /**
     * Invalid source type
     */
    badRequestInvalidSourceType: () => void;

    /**
     * Invalid document URL, or missing for an URL source type
     */
    badRequestInvalidDocumentUrl: () => void;

    /**
     * Prompt addendum too long
     */
    badRequestInvalidPromptAddendum: () => void;

    /**
     * The given rulebookId does not match any rulebook
     */
    badRequestRulebookNotFound: () => void;

    /**
     * The rulebook was received from another connector, so it cannot be modified here
     */
    badRequestRulebookNotEditable: () => void;

    /**
     * That rulebook already has a version named like this one
     */
    badRequestDuplicatedVersion: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetRulebook
 */
export type GetRulebookErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Error handler for ModifyRulebook
 */
export type ModifyRulebookErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid name
     */
    badRequestInvalidName: () => void;

    /**
     * Invalid version
     */
    badRequestInvalidVersion: () => void;

    /**
     * Invalid effective date
     */
    badRequestInvalidEffectiveDate: () => void;

    /**
     * Invalid source type
     */
    badRequestInvalidSourceType: () => void;

    /**
     * Invalid document URL, or missing for an URL source type
     */
    badRequestInvalidDocumentUrl: () => void;

    /**
     * Prompt addendum too long
     */
    badRequestInvalidPromptAddendum: () => void;

    /**
     * The rulebook was received from another connector, so it cannot be modified here
     */
    badRequestRulebookNotEditable: () => void;

    /**
     * The fields describing the document can only be changed while the version is a draft
     */
    badRequestNotADraft: () => void;

    /**
     * That rulebook already has a version named like this one
     */
    badRequestDuplicatedVersion: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Error handler for DeleteRulebook
 */
export type DeleteRulebookErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The version is in force. Activate another version before deleting this one
     */
    badRequestActiveVersion: () => void;

    /**
     * Evaluations kept as evidence were made against this version, so its document has to be kept
     */
    badRequestCitedByEvidence: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Error handler for GetRulebookDocument
 */
export type GetRulebookDocumentErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Error handler for UploadRulebookDocument
 */
export type UploadRulebookDocumentErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The rulebook was received from another connector, so it cannot be modified here
     */
    badRequestRulebookNotEditable: () => void;

    /**
     * The document can only be replaced while the version is a draft
     */
    badRequestNotADraft: () => void;

    /**
     * The version references the document by URL, so there is nothing to upload
     */
    badRequestNoDocumentExpected: () => void;

    /**
     * The client provided no file
     */
    badRequestNoFile: () => void;

    /**
     * The file extension does not match the source type of the version
     */
    badRequestInvalidFormat: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;

    /**
     * The text of the document could not be extracted
     */
    serverErrorTextExtractionFailed: () => void;

    /**
     * The document could not be stored
     */
    serverErrorStorageError: () => void;
};

/**
 * Form parameters for UploadRulebookDocument
 */
export interface UploadRulebookDocumentFormParameters {
    /**
     * Rulebook document to upload - Max 20MB
     */
    document: File;
}

/**
 * Error handler for GetRulebookText
 */
export type GetRulebookTextErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Error handler for ActivateRulebook
 */
export type ActivateRulebookErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The version is already the one in force
     */
    badRequestAlreadyActive: () => void;

    /**
     * The version has no document, so there would be nothing to govern by
     */
    badRequestNoDocument: () => void;

    /**
     * The document published at the given address could not be fetched
     */
    badRequestDocumentNotReachable: () => void;

    /**
     * The address serves a kind of document this connector cannot read
     */
    badRequestDocumentNotSupported: () => void;

    /**
     * The version was received from another connector: it comes into force by accepting it
     */
    badRequestInheritedVersion: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;

    /**
     * The text of the document could not be extracted
     */
    serverErrorTextExtractionFailed: () => void;

    /**
     * The document could not be stored
     */
    serverErrorStorageError: () => void;
};

/**
 * Error handler for DeactivateRulebook
 */
export type DeactivateRulebookErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The version is not the one in force
     */
    badRequestNotActive: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Error handler for AcceptRulebook
 */
export type AcceptRulebookErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Only the version in force, or an inherited version waiting for acceptance, can be accepted
     */
    badRequestNotActive: () => void;

    /**
     * The version was already accepted
     */
    badRequestAlreadyAccepted: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Error handler for ListRules
 */
export type ListRulesErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Query parameters for ListRules
 */
export interface ListRulesQueryParameters {
    /**
     * Narrows the list to a scope
     */
    scope?: string;

    /**
     * Narrows the list to a review status
     */
    status?: string;

    /**
     * Page number (1-based). Default: 1
     */
    page?: number;

    /**
     * Items per page. Default: 10, Max: 256
     */
    pageSize?: number;
}

/**
 * Error handler for CreateRule
 */
export type CreateRuleErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The rulebook was received from another connector, so its rules cannot be added, deleted or reworded here
     */
    badRequestRulebookNotEditable: () => void;

    /**
     * Invalid or missing rule code
     */
    badRequestInvalidCode: () => void;

    /**
     * Invalid scope
     */
    badRequestInvalidScope: () => void;

    /**
     * Invalid or missing text
     */
    badRequestInvalidText: () => void;

    /**
     * Section too long
     */
    badRequestInvalidSection: () => void;

    /**
     * Invalid severity
     */
    badRequestInvalidSeverity: () => void;

    /**
     * Invalid enforcement, or Rego for a rule about usage policies
     */
    badRequestInvalidEnforcement: () => void;

    /**
     * Check expression too long, or not a valid check for a rule that is not enforced by Rego
     */
    badRequestInvalidCheckExpression: () => void;

    /**
     * The rule is approved and its version is in force, so it cannot be deleted
     */
    badRequestRuleInForce: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Error handler for GetRule
 */
export type GetRuleErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Error handler for ModifyRule
 */
export type ModifyRuleErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The rulebook was received from another connector, so its rules cannot be added, deleted or reworded here
     */
    badRequestRulebookNotEditable: () => void;

    /**
     * Invalid or missing rule code
     */
    badRequestInvalidCode: () => void;

    /**
     * Invalid scope
     */
    badRequestInvalidScope: () => void;

    /**
     * Invalid or missing text
     */
    badRequestInvalidText: () => void;

    /**
     * Section too long
     */
    badRequestInvalidSection: () => void;

    /**
     * Invalid severity
     */
    badRequestInvalidSeverity: () => void;

    /**
     * Invalid enforcement, or Rego for a rule about usage policies
     */
    badRequestInvalidEnforcement: () => void;

    /**
     * Check expression too long, or not a valid check for a rule that is not enforced by Rego
     */
    badRequestInvalidCheckExpression: () => void;

    /**
     * The rule is approved and its version is in force, so it cannot be deleted
     */
    badRequestRuleInForce: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Error handler for DeleteRule
 */
export type DeleteRuleErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The rulebook was received from another connector, so its rules cannot be added, deleted or reworded here
     */
    badRequestRulebookNotEditable: () => void;

    /**
     * Invalid or missing rule code
     */
    badRequestInvalidCode: () => void;

    /**
     * Invalid scope
     */
    badRequestInvalidScope: () => void;

    /**
     * Invalid or missing text
     */
    badRequestInvalidText: () => void;

    /**
     * Section too long
     */
    badRequestInvalidSection: () => void;

    /**
     * Invalid severity
     */
    badRequestInvalidSeverity: () => void;

    /**
     * Invalid enforcement, or Rego for a rule about usage policies
     */
    badRequestInvalidEnforcement: () => void;

    /**
     * Check expression too long, or not a valid check for a rule that is not enforced by Rego
     */
    badRequestInvalidCheckExpression: () => void;

    /**
     * The rule is approved and its version is in force, so it cannot be deleted
     */
    badRequestRuleInForce: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Error handler for ExtractRules
 */
export type ExtractRulesErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The given scope is not one of the recognized ones
     */
    badRequestInvalidScope: () => void;

    /**
     * The version has no extracted text, so there is nothing to read
     */
    badRequestNoText: () => void;

    /**
     * An extraction is already running for this version
     */
    badRequestAlreadyRunning: () => void;

    /**
     * The rules were received from another connector, so they are not read again here
     */
    badRequestRulebookNotEditable: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Error handler for ReviewRule
 */
export type ReviewRuleErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The verdict must be either APPROVED or REJECTED
     */
    badRequestInvalidStatus: () => void;

    /**
     * Invalid scope
     */
    badRequestInvalidScope: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

/**
 * Error handler for ReviewRules
 */
export type ReviewRulesErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The verdict must be either APPROVED or REJECTED
     */
    badRequestInvalidStatus: () => void;

    /**
     * Invalid scope
     */
    badRequestInvalidScope: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * There is no rulebook version with that identifier
     */
    notFoundRulebookNotFound: () => void;

    /**
     * There is no rule with that identifier in that rulebook version
     */
    notFoundRuleNotFound: () => void;

    /**
     * The version has no document stored
     */
    notFoundNoDocument: () => void;
};

